import { getNestedSubelementsByType, getNextUuid, getMostRecentFieldValue, getSlsTypesByKey } from '@fs/zion-sls'

/**
 * An array of string constants representing the different types of name parts fields allowed in the Essential Information Card.
 * @type {string[]}
 */
export const namePartTypes = ['NAME_PRE', 'NAME_GN', 'NAME_PATR', 'NAME_MATR', 'NAME_SPRE', 'NAME_SURN', 'NAME_SUF']

/**
 * Creates default name parts structure for the Essential Information Card
 * @param {boolean} includeValues - Whether to include default values
 * @returns {Object} Object containing default name part field structures
 */
export const createDefaultNameParts = (includeValues = true) => ({
  NAME_GN: { value: includeValues ? '' : undefined },
  NAME_SURN: { value: includeValues ? '' : undefined },
})

export const personDetailTypes = ['SEX_CODE', 'RACE_OR_COLOR', 'AGE']

export const createDefaultNameTemplate = (forEmptyForm = false) => ({
  id: getNextUuid(),
  added: true,
  type: forEmptyForm ? null : {},
  parts: forEmptyForm ? [] : ['NAME_GN', 'NAME_SURN'],
  removedParts: [],
  ...(forEmptyForm ? {} : createDefaultNameParts(true)),
})

// Legacy exports for backward compatibility
export const DEFAULT_NAME_PARTS = createDefaultNameParts(true)
export const DEFAULT_NAME_TEMPLATE = createDefaultNameTemplate(false)

export const sortNamePartTypes = (a, b, key) =>
  namePartTypes.indexOf(key ? a[key] : a) - namePartTypes.indexOf(key ? b[key] : b)

export const getSlsNameTypeByKey = (slsTypes, key) => {
  const slsNameTypes = getSlsTypesByKey(slsTypes, 'names')
  return slsNameTypes.find((type) => type.nameType === key)
}

export const prepareNameParts = (name) => {
  const availableNameParts = namePartTypes.reduce((acc, partType) => {
    const part = getNestedSubelementsByType(name, partType, true)?.[0]

    if (part) {
      // part is the intermediate element (e.g., NAME_GIVEN, NAME_SURNAME)
      const field = part.subElements?.[0] // The field element inside the intermediate element
      const elementId = part.id || getNextUuid()
      acc[partType] = {
        elementId, // ID of the intermediate element (for REM_SUB)
        fieldId: field?.id || getNextUuid(), // ID of the field element (for ADD_FV)
        added: !part.id,
        type: part?.fieldType,
        removed: false,
        value: getMostRecentFieldValue(part),
      }
    }
    return acc
  }, {})

  return availableNameParts
}

export const prepareNames = (names, slsTypes, allowEmptyForm = false) => {
  // For completely empty forms, don't create default template unless explicitly requested
  if (!names || names.length === 0) {
    return allowEmptyForm ? [] : [createDefaultNameTemplate(false)]
  }

  return names.map((name) => {
    const slsNameType = getSlsNameTypeByKey(slsTypes, name?.nameType) || {}
    const partTypes =
      name?.subElements
        ?.flatMap((part) => part?.subElements?.flatMap((subPart) => subPart?.fieldType))
        .filter(Boolean) || []

    return {
      id: name?.id || getNextUuid(),
      added: !name?.id,
      removed: false,
      type: { key: slsNameType?.nameType, primaryText: slsNameType?.displayLabel },
      parts: partTypes,
      ...prepareNameParts(name),
    }
  })
}

export const getFieldValue = (person, fieldType) => {
  const field = getNestedSubelementsByType(person, fieldType)?.[0] || null
  return {
    field,
    value: getMostRecentFieldValue(field),
  }
}
